import React, { useState, useEffect } from 'react';
import { Upload } from './universe';
import { Input, Drawer, MessagePlugin } from 'tdesign-react';
import { useMenuStore, useImageStore } from '../../store';
import { isLogin } from '../../utils/index.js';
import {
  DEFAULT_ACCEPT_LIST,
  DEFAULT_IMAGE_PREFIX,
  DEFAULT_MAX_FILE_SIZE,
  MESSAGE_DURATION,
  DEFAULT_MESSAGES,
  REGEX,
} from '../../const/constants.js';

const URL_EXP = REGEX.URL;

/**
 * 将 File 转为 base64
 * @param {File} file
 * @returns {Promise<string>}
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function showErrorMsg(msg) {
  MessagePlugin.error({
    content: msg,
    duration: MESSAGE_DURATION,
    className: 'ocrDemo-messgae-error',
    icon: <span className="ocrDemo-icon-errorMsg" />,
  });
}

function getFileExt(name = '') {
  return name.split('.').pop().toLowerCase();
}

function stripBase64Prefix(base64, ext) {
  if (ext === 'bmp') return base64.replace('data:image/bmp;base64,', '');
  if (['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'md', 'txt', 'pdf'].includes(ext)) {
    return base64.replace(/data.+?;base64,/, '');
  }
  return base64;
}

function isPreviewable(page) {
  // pdf 无法用 <img> 预览，图片类才能预览
  if (!page) return false;
  const ext = (page.fileType || '').toLowerCase();
  if (ext === 'pdf') return false;
  return true;
}

/**
 * 多图上传合并卡片 + 抽屉预览
 * 展示位置：上传文件 tab 下方缩略图区
 */
export function MultiUploadCard() {
  const multiUploadPages = useImageStore((s) => s.multiUploadPages);
  const clearMultiUploadPages = useImageStore((s) => s.clearMultiUploadPages);
  const removeMultiUploadPage = useImageStore((s) => s.removeMultiUploadPage);
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (!multiUploadPages || multiUploadPages.length === 0) return null;

  const total = multiUploadPages.length;
  const firstPage = multiUploadPages[0];
  const firstPreviewable = isPreviewable(firstPage);

  return (
    <>
      <div className="ocrDemo_multiUpload__cardWrap">
        <div className="ocrDemo_multiUpload__cardBox">
          <div
            className="ocrDemo_multiUpload__card"
            onClick={() => setDrawerOpen(true)}
          >
            {firstPreviewable ? (
              <img
                src={firstPage.previewUrl}
                alt="preview"
                className="ocrDemo_multiUpload__cardImg"
              />
            ) : (
              <div className="ocrDemo_multiUpload__cardFile">
                <span className="ocrDemo_multiUpload__cardFileIcon" />
                <span className="ocrDemo_multiUpload__cardFileExt">
                  {(firstPage.fileType || 'FILE').toUpperCase()}
                </span>
              </div>
            )}
            <div className="ocrDemo_multiUpload__cardLabel">
              <span className="ocrDemo_multiUpload__cardLabelIcon" />
              多图
            </div>
            <div className="ocrDemo_multiUpload__cardBadge">
              <span className="ocrDemo_multiUpload__cardBadgeIcon" />
              {total} 页
            </div>
          </div>
          <div
            className="ocrDemo_multiUpload__cardDelete"
            onClick={(e) => {
              e.stopPropagation();
              clearMultiUploadPages();
            }}
          >
            ×
          </div>
        </div>
      </div>

      <Drawer
        header={`多图上传 · ${total} 张 · 共 ${total} 页`}
        visible={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        size="520px"
        footer={null}
      >
        <div className="ocrDemo_multiUpload__pages">
          {multiUploadPages.map((page, index) => (
            <div className="ocrDemo_multiUpload__pageItem" key={page.uid || index}>
              <div className="ocrDemo_multiUpload__pageHeader">
                <span className="ocrDemo_multiUpload__pageTitle">
                  第 {index + 1} 页
                </span>
                <span
                  className="ocrDemo_multiUpload__pageRemove"
                  onClick={() => removeMultiUploadPage(index)}
                >
                  删除
                </span>
              </div>
              {isPreviewable(page) ? (
                <img
                  src={page.previewUrl}
                  alt={`page-${index + 1}`}
                  className="ocrDemo_multiUpload__pageImg"
                />
              ) : (
                <div className="ocrDemo_multiUpload__pageNoPreview">
                  {(page.fileType || 'FILE').toUpperCase()} 文件暂不支持预览
                </div>
              )}
            </div>
          ))}
        </div>
      </Drawer>
    </>
  );
}

/**
 * 多图上传拖拽区 + URL 输入
 * 展示位置：底部（替代单图上传组件）
 */
export function MultiUploadDropzone() {
  const currentMenu = useMenuStore((s) => s.currentMenu);
  const multiUploadPages = useImageStore((s) => s.multiUploadPages);
  const addMultiUploadPages = useImageStore((s) => s.addMultiUploadPages);
  const setCurrentImage = useImageStore((s) => s.setCurrentImage);
  const setImgListType = useImageStore((s) => s.setImgListType);
  const setTotalPage = useImageStore((s) => s.setTotalPage);
  const setIsImageError = useImageStore((s) => s.setIsImageError);

  const acceptList = currentMenu?.acceptList || DEFAULT_ACCEPT_LIST;
  const prefix = currentMenu?.prefix || DEFAULT_IMAGE_PREFIX;
  const maxLength = currentMenu?.maxLength || DEFAULT_MAX_FILE_SIZE;

  const [imgUrl, setImgUrl] = useState('');

  const acceptStr = acceptList.map((e) => `.${e}`).join(',');

  // 页列表变化时，同步一个"合成的 currentImage"，供右侧参数面板和"开始识别"按钮使用
  useEffect(() => {
    if (!multiUploadPages || multiUploadPages.length === 0) return;
    const first = multiUploadPages[0];
    const listPrefix =
      first.type === 'url' ? `${prefix}UrlList` : `${prefix}Base64List`;
    setCurrentImage({
      isMultiUpload: true,
      multiPages: multiUploadPages,
      // 让预览侧的多页 Thumbnail 复用 pdfImgList 结构（每项含 ImageUrl）
      pdfImgList: multiUploadPages.map((p, idx) => ({
        ImageUrl: p.previewUrl,
        pageNum: idx + 1,
      })),
      previewUrl: first.previewUrl,
      url: first.url,
      OriginUrl: first.OriginUrl,
      fileType: first.fileType,
      // prefix 仅用于 Request 展示（实际发送时按 List key 覆盖）
      prefix: first.prefix,
      // 供 GeneralBasicOCR Async 分支识别的 List key
      listPrefix,
      isExample: false,
      cantPreview: false,
      type: first.type,
      pageNum: 1,
    });
    setTotalPage(multiUploadPages.length);
    setImgListType('upload');
    setIsImageError(false);
  }, [
    multiUploadPages,
    prefix,
    setCurrentImage,
    setTotalPage,
    setImgListType,
    setIsImageError,
  ]);

  /**
   * 校验并合并新增页：拒绝跨类型（url/base64）混批
   * @param {Array} newPages 新增页
   * @returns {boolean}
   */
  const guardMixType = (newPages) => {
    if (!multiUploadPages || multiUploadPages.length === 0) return true;
    const existingType = multiUploadPages[0].type;
    const bad = newPages.some((p) => p.type !== existingType);
    if (bad) {
      showErrorMsg('同一次多图上传不支持混合 URL 和本地文件，请分别提交');
      return false;
    }
    return true;
  };

  const beforeUpload = (file) => {
    return new Promise(async (_, reject) => {
      try {
        await isLogin();
        if (!file) {
          showErrorMsg(DEFAULT_MESSAGES.SELECT_FILE);
          return reject();
        }
        const ext = getFileExt(file.name);
        if (!acceptList.includes(ext)) {
          showErrorMsg(
            currentMenu?.uploadExtErrorMsg ? (
              <>
                体验页面不支持{ext}格式，请点击
                <a
                  style={{ color: '#4A70FF' }}
                  target="_blank"
                  rel="noreferrer"
                  href={currentMenu.url}
                >
                  {currentMenu.title}
                </a>
                调用API接口
              </>
            ) : (
              DEFAULT_MESSAGES.INVALID_FILE_FORMAT
            )
          );
          return reject();
        }
        const base64 = await fileToBase64(file);
        if (base64?.length > maxLength * 1024 * 1024) {
          showErrorMsg(
            `${DEFAULT_MESSAGES.FILE_TOO_LARGE_BASE641}${maxLength}M${DEFAULT_MESSAGES.FILE_TOO_LARGE_BASE642}`
          );
          return reject();
        }
        const stripped = stripBase64Prefix(base64, ext);
        const newPage = {
          uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          type: 'base64',
          previewUrl: base64,
          OriginUrl: stripped,
          url: stripped,
          base64prefix: '',
          prefix: `${prefix}Base64`,
          fileType: ext,
          isExample: false,
          pageNum: 1,
          cantPreview: false,
          name: file.name,
          size: file.size,
        };
        if (!guardMixType([newPage])) {
          return reject();
        }
        addMultiUploadPages([newPage]);
      } catch (err) {
        // ignore
      }
      // 始终 reject 阻断 Upload 组件的默认 request
      return reject();
    });
  };

  const handleCheckUrl = () => {
    const url = imgUrl.trim();
    if (!URL_EXP.test(url)) {
      showErrorMsg(DEFAULT_MESSAGES.INVALID_URL);
      return;
    }
    const uploadExtList = currentMenu?.uploadExtList || [];
    const allExts = uploadExtList.concat(acceptList);
    const matched = allExts.filter((e) => url.toLowerCase().indexOf(e) > -1);
    const ext = matched.length > 0 ? matched[matched.length - 1] : '';
    if (!acceptList.includes(ext)) {
      showErrorMsg(DEFAULT_MESSAGES.INVALID_FILE_FORMAT);
      return;
    }
    const newPage = {
      uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: 'url',
      url,
      OriginUrl: url,
      previewUrl: url,
      prefix: `${prefix}Url`,
      fileType: ext,
      isExample: false,
      pageNum: 1,
      cantPreview: false,
    };
    if (!guardMixType([newPage])) return;
    addMultiUploadPages([newPage]);
    setImgUrl('');
  };

  const hasPages = multiUploadPages && multiUploadPages.length > 0;
  const acceptDisplay = acceptList.map((e) => e.toUpperCase()).join(' / ');

  return (
    <div className="ocrDemo_multiUpload">
      {hasPages && (
        <div className="ocrDemo_multiUpload__banner">
          <span className="ocrDemo_multiUpload__bannerIcon" />
          <span>
            多页/多图模式：当前共 {multiUploadPages.length} 页（多张图片），仍作为
            1 次批改任务一次性提交，右侧结果可按页联动查看
          </span>
        </div>
      )}

      <Upload multiple beforeUpload={beforeUpload} accept={acceptStr}>
        <div className="ocrDemo_multiUpload__dropzone">
          <div className="ocrDemo_multiUpload__dropzoneIcon" />
          <div className="ocrDemo_multiUpload__dropzoneTitle">
            点击或拖拽上传：支持单张图片，或一次选择多张图片/多页 PDF
          </div>
          <div className="ocrDemo_multiUpload__dropzoneDesc">
            单图或多页均作为 1
            次批改任务一次性提交，结果与左侧多页联动查看；PDF 自动逐页拆分为图片；支持{' '}
            {acceptDisplay}
          </div>
        </div>
      </Upload>

      <div className="ocrDemo_multiUpload__urlRow">
        <Input
          prefixIcon={<span className="ocrDemo-icon_search" />}
          suffixIcon={
            <span className="ocrDemo-icon_enter" onClick={handleCheckUrl} />
          }
          style={{ width: '100%' }}
          value={imgUrl}
          onEnter={handleCheckUrl}
          onChange={(v) => setImgUrl(v)}
          placeholder={`输入在线URL,回车发起调用，支持${acceptList.join(
            '、'
          )}等格式，大小不超过${maxLength}M`}
        />
      </div>
    </div>
  );
}

export default MultiUploadDropzone;
